import React, { useState } from 'react';
import { ChevronDown, ArrowRight } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { useModal } from '../context/ModalContext';

const faqs = [
  {
    id: '01',
    question: 'What does an AI engagement with Synentia usually include?',
    answer:
      'Every engagement starts with a free audit — we map your operations, find where AI creates real value, and tell you honestly what\'s worth building. From there: scoped build, go-live, and a monthly retainer where we operate and improve what we built.',
  },
  {
    id: '02',
    question: 'How quickly can we launch the first production use case?',
    answer:
      'Most focused builds go live in 4–8 weeks from signed agreement. We scope specifically to avoid over-engineering — the first use case ships fast and proves value before anything else is committed.',
  },
  {
    id: '03',
    question: 'Do you work with our existing stack and model providers?',
    answer:
      'Yes — we build around what you already have. We\'re model-agnostic and integration-first. If your team uses specific CRMs, ERPs, or automation platforms, we connect to them.',
  },
  {
    id: '04',
    question: 'How do you handle privacy, governance, and security?',
    answer:
      'Security is built in from day one. For regulated environments we execute BAAs, align to HIPAA, SOC 2, GDPR, and DPDP, and build with encryption, role-based access, and full audit logging.',
  },
  {
    id: '05',
    question: 'Can you train our team, not just deliver the solution?',
    answer:
      'Yes — every engagement includes a go-live walkthrough, operating documentation, and a handoff your team can actually use. You should be able to understand and oversee what we build.',
  },
  {
    id: '06',
    question: 'What outcomes do clients typically see first?',
    answer:
      'The fastest wins are time savings on manual processes — measurable within the first 30–60 days. Revenue recovery and quality improvements follow as the system runs longer. We track metrics from day one.',
  },
];

const FAQ = () => {
  const [openItem, setOpenItem] = useState(faqs[0].id);
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.12 });
  const { openDemoModal } = useModal();

  return (
    <section id="faq" className="relative py-20 sm:py-24 lg:py-32 bg-[--bg-primary]">
      <div className="absolute top-0 left-0 right-0 section-divider" />

      <div ref={sectionRef} className="max-w-[1500px] mx-auto px-4 sm:px-6">
        <div
          className={`max-w-4xl mb-14 transition-all duration-700 ease-out ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}
        >
          <p className="section-eyebrow mb-4">FAQ</p>
          <h2 className="text-[clamp(2rem,4.1vw,3.8rem)] font-semibold text-[--text-primary] leading-[1.02]">
            Questions We Hear a Lot
          </h2>
          <p className="mt-6 text-lg text-[--text-secondary] max-w-3xl">
            Clear answers on delivery, governance, timelines, and adoption so you can evaluate
            fit quickly.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_330px] gap-5">
          <div className="space-y-3">
            {faqs.map((faq, index) => {
              const isOpen = openItem === faq.id;

              return (
                <div
                  key={faq.id}
                  className={`soft-card rounded-2xl transition-all duration-500 ${
                    isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                  style={{ transitionDelay: `${160 + index * 70}ms` }}
                >
                  <button
                    className="w-full text-left px-6 py-5 flex items-start gap-4"
                    onClick={() => setOpenItem(isOpen ? '' : faq.id)}
                  >
                    <span className="text-[11px] font-mono text-[--accent] uppercase tracking-[0.17em] pt-1">
                      {faq.id}
                    </span>
                    <span className="flex-1 text-[--text-primary] text-base md:text-lg font-medium leading-snug">
                      {faq.question}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-[--text-secondary] transition-transform duration-300 ${
                        isOpen ? 'rotate-180 text-[--accent]' : ''
                      }`}
                    />
                  </button>

                  <div
                    className={`grid transition-all duration-400 ease-out ${
                      isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="px-6 pb-6 pl-6 sm:pl-[4.7rem] text-[--text-secondary] leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <aside
            className={`soft-card rounded-2xl p-6 h-fit transition-all duration-700 ease-out delay-500 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <p className="section-eyebrow mb-4">Still evaluating?</p>
            <h3 className="text-xl font-semibold text-[--text-primary] leading-tight">
              Bring your use case.
              <br />
              We can map a practical rollout.
            </h3>
            <p className="mt-4 text-sm text-[--text-secondary] leading-relaxed">
              We will review your workflow, data, and constraints and outline a phased implementation
              plan with clear milestones.
            </p>
            <button
              onClick={openDemoModal}
              className="mt-5 px-5 py-2 text-[11px] font-medium text-[--bg-primary] uppercase tracking-[0.18em] font-mono bg-[--accent] rounded-full hover:bg-[--accent-hover] transition-colors flex items-center gap-2 btn-pattern"
            >
              Talk to Us
              <ArrowRight className="w-3 h-3" />
            </button>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
